import type { CreateBulkEmailPayload } from "@/schema"
import { useMutation, useQueryClient } from "@tanstack/react-query"

import { useApiContext } from "@/hooks/api/use-api-context"

import { cancelBulkEmail, cancelBulkEmailRecipient, createBulkEmail } from "./api"

export function useCreateBulkEmail() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: CreateBulkEmailPayload) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return createBulkEmail(context, payload)
    },
    meta: { errorToast: { fallback: "Failed to send bulk email" } },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bulk_emails"] })
    },
  })
}

export function useCancelBulkEmail() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return cancelBulkEmail(context, id)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bulk_emails"] })
    },
  })
}

export function useCancelBulkEmailRecipient(bulkEmailId: string) {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (recipientId: string) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return cancelBulkEmailRecipient(context, bulkEmailId, recipientId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bulk_email", bulkEmailId] })
      queryClient.invalidateQueries({ queryKey: ["bulk_emails"] })
    },
  })
}
